import { __spreadArrays } from "tslib";
import React from 'react';
import { cx } from '@skbkontur/react-ui/lib/theming/Emotion';
import { isIE11 } from '@skbkontur/react-ui/lib/client';
import { ThemeContext } from '@skbkontur/react-ui/lib/theming/ThemeContext';
import { isTestEnv } from '@skbkontur/react-ui/lib/currentEnvironment';
import { SpinnerFallbackAnimationRunner, createOffsetAnimation, createLengthAnimation, createColorAnimation, createRotationAnimation } from '../../components/Spinner/SpinnerFallbackAnimation';
import { jsStyles } from './SpinnerIcon.styles';
var CLOUD_SVG_PATH = "M32.0297086,9.1495774 L31.5978628,8.5870774 C29.3570968,\n      5.67148577 25.9305165,4 22.1999329,4 C17.3429265,\n      4 12.9026663,7.04270463 11.154144,11.5717304 L10.901479,\n      12.2253114 L10.2421341,12.4725311 C6.50853057,13.8727758 4,\n      17.4719751 4,21.428492 C4,26.7061833 8.32047079,\n      31 13.6314689,31 L32.0297086,31 C38.078569,31 43,26.1036477 43,\n      20.0862989 C43,14.3602091 38.493302,9.5769573 32.7403918,\n      9.19661922 L32.0297086,9.1495774 Z";
export var SPINNER_CLOUD_SIZE = {
  height: 35,
  width: 47
};

var CircleIcon = function CircleIcon(_a) {
  var className = _a.className,
      dimmed = _a.dimmed;
  var svgRef = React.useRef(null);

  if (isIE11 && !isTestEnv) {
    // This condition will not change during app's life time
    // So its OK to use hooks here
    // https://reactjs.org/docs/hooks-rules.html#only-call-hooks-at-the-top-level
    // eslint-disable-next-line react-hooks/rules-of-hooks
    var fallbackAnimationRef_1 = React.useRef(null); // eslint-disable-next-line react-hooks/rules-of-hooks

    var _b = React.useContext(ThemeContext),
        red_1 = _b.red,
        yellow_1 = _b.yellow,
        green_1 = _b.green,
        brand_1 = _b.brand; // eslint-disable-next-line react-hooks/rules-of-hooks


    React.useEffect(function () {
      var setStyleProperty = function setStyleProperty() {
        var _a;

        var args = [];

        for (var _i = 0; _i < arguments.length; _i++) {
          args[_i] = arguments[_i];
        }

        var svg = svgRef.current;

        if (svg) {
          (_a = svg.style).setProperty.apply(_a, args);
        }
      };

      fallbackAnimationRef_1.current = new SpinnerFallbackAnimationRunner(__spreadArrays([createOffsetAnimation(0, -230, 1000, setStyleProperty, '%'), createLengthAnimation([62, 168], [187, 43], 2000, setStyleProperty, '%'), createRotationAnimation(0, 360, 2000, setStyleProperty)], dimmed ? [] : [createColorAnimation([red_1, yellow_1, green_1, brand_1], 1500, setStyleProperty)]), 1000 / 60);
      return function () {
        var fallbackAnimation = fallbackAnimationRef_1.current;
        var svg = svgRef.current;

        if (fallbackAnimation) {
          fallbackAnimation.stop();
        }

        if (svg) {
          svg.removeAttribute('style');
        }
      };
    }, [dimmed, red_1, yellow_1, green_1, brand_1]);
  }

  return React.createElement("span", {
    className: jsStyles.root()
  }, React.createElement("svg", {
    className: cx(jsStyles.icon(), className),
    width: "16",
    height: "16",
    fill: "none",
    strokeDasharray: "10, 27",
    strokeDashoffset: "0",
    strokeWidth: "1.5px",
    ref: svgRef
  }, React.createElement("circle", {
    cx: "8",
    cy: "8",
    r: "6"
  })));
};

var CloudIcon = function CloudIcon(_a) {
  var size = _a.size,
      className = _a.className,
      strokeClassName = _a.strokeClassName,
      dimmed = _a.dimmed;
  var pathRef = React.useRef(null);

  if (isIE11 && !isTestEnv) {
    // This condition will not change during app's life time
    // So its OK to use hooks here
    // https://reactjs.org/docs/hooks-rules.html#only-call-hooks-at-the-top-level
    // eslint-disable-next-line react-hooks/rules-of-hooks
    var fallbackAnimationRef_2 = React.useRef(null); // eslint-disable-next-line react-hooks/rules-of-hooks

    var _b = React.useContext(ThemeContext),
        red_2 = _b.red,
        yellow_2 = _b.yellow,
        green_2 = _b.green,
        brand_2 = _b.brand; // eslint-disable-next-line react-hooks/rules-of-hooks


    React.useEffect(function () {
      if (isIE11 && !isTestEnv) {
        var setStyleProperty = function setStyleProperty() {
          var _a;

          var args = [];

          for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
          }

          var path = pathRef.current;

          if (path) {
            (_a = path.style).setProperty.apply(_a, args);
          }
        };

        fallbackAnimationRef_2.current = new SpinnerFallbackAnimationRunner(__spreadArrays([createOffsetAnimation(10, 116, 1000, setStyleProperty), createLengthAnimation([10, 96], [50, 56], 2000, setStyleProperty)], dimmed ? [] : [createColorAnimation([red_2, yellow_2, green_2, brand_2], 1500, setStyleProperty)]), 1000 / 60);
        return function () {
          var fallbackAnimation = fallbackAnimationRef_2.current;
          var path = pathRef.current;

          if (fallbackAnimation) {
            fallbackAnimation.stop();
          }

          if (path) {
            path.removeAttribute('style');
          }
        };
      }
    }, [dimmed, red_2, yellow_2, green_2, brand_2]);
  }

  var multiply = size === 'big' ? 2 : 1;
  return React.createElement("svg", {
    width: SPINNER_CLOUD_SIZE.width * multiply,
    height: SPINNER_CLOUD_SIZE.height * multiply,
    viewBox: "0 0 47 35"
  }, React.createElement("path", {
    d: CLOUD_SVG_PATH,
    strokeWidth: "2",
    className: strokeClassName
  }), React.createElement("path", {
    d: CLOUD_SVG_PATH,
    strokeWidth: "2",
    className: className,
    ref: pathRef
  }));
};

export var SpinnerIcon = function SpinnerIcon(_a) {
  var size = _a.size,
      strokeClassName = _a.strokeClassName,
      className = _a.className,
      dimmed = _a.dimmed;
  return size === 'mini' ? React.createElement(CircleIcon, {
    className: className,
    dimmed: dimmed
  }) : React.createElement(CloudIcon, {
    size: size,
    strokeClassName: strokeClassName,
    className: className,
    dimmed: dimmed
  });
};